import React, { useState, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useAuth } from '../contexts/AuthContext'
import { getHeaders } from '../utils/csrf'
import FacebookLoginButton from '../components/FacebookLoginButton'
import PasswordInput from '../components/common/PasswordInput'
import { trackEngagement } from '../utils/analytics'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { formContainer, formItem, alertSlideIn, authCard, buttonTap, formShake } from '../config/animations'

export default function LoginPage() {
  usePageTitle('Sign In')
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loginWithFacebook } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [shakeKey, setShakeKey] = useState(0)
  const formRef = useRef(null)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const headers = await getHeaders()
      const response = await fetch('/users/sign_in', {
        method: 'POST',
        headers,
        body: JSON.stringify({ user: formData }),
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json()
        login(data)
        trackEngagement.userLogin()
        const from = location.state?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else {
        const data = await response.json()
        if (data.error === 'Invalid Email or password.') {
          setError('Invalid email or password. Please try again.')
        } else {
          setError(data.error || 'Failed to sign in')
        }
        setShakeKey(k => k + 1)
      }
    } catch (error) {
      setError('An error occurred while signing in. Please check your connection.')
      setShakeKey(k => k + 1)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFacebookLogin = async () => {
    try {
      await loginWithFacebook()
      trackEngagement.userLogin()
    } catch (error) {
      setError('Failed to initiate Facebook login')
    }
  }

  return (
    <div className="auth-page">
      <motion.div
        {...authCard}
        className="w-full max-w-md relative z-10"
      >
        {/* Branding header */}
        <motion.div
          variants={formItem}
          initial="initial"
          animate="animate"
          className="text-center mb-8"
        >
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl
              bg-gradient-to-br from-primary-100 to-primary-200
              dark:from-primary-100 dark:to-primary-200
              shadow-sm mb-5"
            whileHover={{ scale: 1.05, rotate: 3 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          >
            <MapPinIcon className="w-8 h-8 text-primary-600 dark:text-primary-600" />
          </motion.div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text">
            Welcome back
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            Sign in to find garage sales near you
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          key={shakeKey}
          animate={shakeKey > 0 ? formShake.animate : {}}
          className="auth-card"
          ref={formRef}
        >
          {/* Error alert */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                {...alertSlideIn}
                className="alert-danger"
                role="alert"
              >
                <ExclamationTriangleIcon className="alert-icon text-red-500 dark:text-red-500" />
                <p>{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <motion.form
            variants={formContainer}
            initial="initial"
            animate="animate"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Email field */}
            <motion.div variants={formItem} className="input-group mb-5">
              <label htmlFor="login-email" className="input-label">
                Email address
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                disabled={isLoading}
                className={`input-field-enhanced ${error ? 'has-error' : ''}`}
              />
            </motion.div>

            {/* Password field */}
            <motion.div variants={formItem} className="input-group mb-2">
              <label htmlFor="login-password" className="input-label">
                Password
              </label>
              <PasswordInput
                id="login-password"
                name="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={isLoading}
                className={`input-field-enhanced ${error ? 'has-error' : ''}`}
              />
            </motion.div>

            {/* Forgot password link */}
            <motion.div variants={formItem} className="mb-6 text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 dark:text-primary-600
                  hover:text-primary-700 dark:hover:text-primary-700
                  transition-colors"
              >
                Forgot password?
              </Link>
            </motion.div>

            {/* Submit button */}
            <motion.div variants={formItem}>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileTap={!isLoading ? buttonTap : undefined}
                whileHover={!isLoading ? { scale: 1.01 } : undefined}
                className="btn-primary w-full min-h-[52px] text-base font-semibold
                  shadow-md hover:shadow-lg transition-shadow"
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </motion.button>
            </motion.div>
          </motion.form>

          {/* Divider */}
          <motion.div
            variants={formItem}
            initial="initial"
            animate="animate"
            className="relative my-7"
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-bg-elevated text-text-muted font-medium">
                Or continue with
              </span>
            </div>
          </motion.div>

          {/* Facebook login */}
          <motion.div
            variants={formItem}
            initial="initial"
            animate="animate"
          >
            <FacebookLoginButton />
          </motion.div>
        </motion.div>

        {/* Register link */}
        <motion.p
          variants={formItem}
          initial="initial"
          animate="animate"
          className="text-center mt-8 text-base text-text-secondary"
        >
          Don't have an account?{' '}
          <Link
            to="/register"
            className="font-semibold text-primary-600 dark:text-primary-600
              hover:text-primary-700 dark:hover:text-primary-700
              underline underline-offset-2 decoration-primary-300 dark:decoration-primary-400
              hover:decoration-primary-500 dark:hover:decoration-primary-500
              transition-colors"
          >
            Create one free
          </Link>
        </motion.p>
      </motion.div>
    </div>
  )
}
